/**
 * Linkify utility - detects and converts URLs in text to clickable links
 */

// URL regex pattern - matches http(s), www, and common TLDs
const urlPattern = /(\b(https?:\/\/|www\.)[^\s<>[\]{}|\\^`"']+|(?:[a-zA-Z0-9-]+\.)+(?:com|org|net|edu|gov|io|co|app|dev|info|biz|me|us|uk|ca|au|de|fr|es|it|nl|ru|jp|cn|in|br|mx)(?:\/[^\s<>[\]{}|\\^`"']*)?)/gi;

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} text - The text to escape
 * @returns {string} - Escaped text
 */
export function escapeHtml(text) {
  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}

/**
 * Convert URLs in text to clickable HTML links
 * @param {string} text - The text to process
 * @param {Object} options - Options for link creation
 * @param {string} options.target - Link target (default: '_blank')
 * @param {string} options.rel - Link rel attribute (default: 'noopener noreferrer')
 * @param {string} options.className - CSS class for links (default: 'message-link')
 * @returns {string} - HTML string with clickable links
 */
export function linkify(text, options = {}) {
  if (!text) return "";

  const {
    target = "_blank",
    rel = "noopener noreferrer",
    className = "message-link",
  } = options;

  // First escape HTML to prevent XSS
  const escapedText = escapeHtml(text);

  // Then convert URLs to links
  return escapedText.replace(urlPattern, (url) => {
    // Add protocol if missing
    let href = url;
    if (!href.match(/^https?:\/\//i)) {
      href = "https://" + href;
    }

    return `<a href="${href}" target="${target}" rel="${rel}" class="${className}">${url}</a>`;
  });
}

/**
 * Check if a string contains any URLs
 * @param {string} text - The text to check
 * @returns {boolean} - True if text contains URLs
 */
export function containsUrl(text) {
  if (!text) return false;
  return urlPattern.test(text);
}

/**
 * Extract all URLs from text
 * @param {string} text - The text to extract URLs from
 * @returns {string[]} - Array of found URLs
 */
export function extractUrls(text) {
  if (!text) return [];
  const matches = text.match(urlPattern);
  return matches || [];
}

export default {
  linkify,
  escapeHtml,
  containsUrl,
  extractUrls,
};
